<template>
	<v-select
		:model-value="selectedStation"
		:items="stationsItems"
		item-title="StationName"
		item-value="StationID"
		return-object
		hide-details
		:hide-no-data="false"
		variant="solo"
		density="compact"
		flat
		single-line
		class="station-menu-container text-body-1 pa-0"
		:menu="selectorOpen"
		menu-icon=""
		:disabled="stationConnectedToStudio || (!mainSelector && liveMode)"
		:menu-props="menuProps"
		@update:model-value="selectStation"
		@click="focusOnSelectorInput"
	>
		<template #label>
			<div class="d-flex align-center" :style="`visibility: ${!selectedStation ? 'visible' : 'hidden'}`">
				<v-avatar :size="mainSelector && xlAndUpScreen ? 'large' : 32" class="mr-2">
					<v-img v-if="config.ORIGIN === 'SOUNDPLUS'" class="logo" />
					<v-img v-else :src="`${config.PORTAL_URL}/content/accounts/${organizationId}/image`" eager />
				</v-avatar>
				<div class="text-uppercase text-body-1">Select station</div>
				<v-icon size="24" class="ml-2" icon="mdi-chevron-down-circle" />
			</div>
		</template>
		<template #prepend-item>
			<v-list-item height="38">
				<template #default>
					<v-text-field
						v-model="stationSearchTerm"
						variant="outlined"
						density="compact"
						clearable
						single-line
						hide-details
						append-inner-icon="mdi-magnify"
						label="Filter"
						autofocus
						@update:model-value="loadStations()"
						@click.stop
						@click:clear="loadStations(0, true)"
						@keydown.stop
					/>
				</template>
			</v-list-item>
		</template>
		<template #no-data>
			<v-list-item disabled>
				<v-list-item-title class="text-disabled text-body-1">
					<div v-if="stations.loading">
						<v-progress-circular indeterminate size="16" width="2" class="mr-1 mb-1" />
						Loading stations...
					</div>
					<div v-else>No stations found.</div>
				</v-list-item-title>
			</v-list-item>
		</template>
		<template #item="{item, props}">
			<v-list-item v-bind="props" class="py-2">
				<template #prepend>
					<v-avatar :size="mainSelector && xlAndUpScreen ? 'large' : 32" :image="item.raw.logo" />
				</template>
				<template #title>
					<div
						class="text-body-1 text-wrap"
						:class="{'font-weight-bold': item.title === selectedStation?.StationName}"
					>
						{{ item.title }}
					</div>
				</template>
				<template v-if="item.raw.favorite" #append>
					<v-icon size="24" icon="mdi-star" />
				</template>
			</v-list-item>
		</template>
		<template #selection="{item, props}">
			<div v-bind="props" class="d-flex align-center w-100" style="max-height: 3em; min-width: 100px">
				<v-avatar
					:size="mainSelector && xlAndUpScreen ? 'large' : 32"
					:image="item.raw.Logo"
					class="mr-2"
					style="border: 2px solid"
				/>
				<v-tooltip location="bottom" :text="item.title">
					<template #activator="{props}">
						<div
							v-bind="props"
							class="text-h1 mr-2 station-name font-weight-bold text-truncate"
							:class="mainSelector ? 'text-h1' : 'text-h3'"
						>
							{{ item.title }}
						</div>
					</template>
				</v-tooltip>
				<v-icon size="24" icon="mdi-chevron-down-circle" />
			</div>
		</template>
		<template v-if="hasMore" #append-item>
			<div v-intersect="loadMore">
				<v-list-item>
					<v-list-item-title class="d-inline-flex text-disabled text-body-1">
						<v-progress-circular indeterminate color="primary" size="16" width="2" class="mx-2 my-1" />
						Loading more stations...
					</v-list-item-title>
				</v-list-item>
			</div>
		</template>
	</v-select>
</template>

<script>
import config from "@/config";

/* Libraries */
import _ from "lodash";
import emitter from "@/emitter";

/* Mixins */
import SequencerPlayerStudioStatusMixin from "@/mixins/SequencerPlayerStudioStatus";
import StationSelectionMixin from "@/mixins/StationSelectionMixin";

export default {
	name: "StationSelector",

	mixins: [SequencerPlayerStudioStatusMixin, StationSelectionMixin],

	props: {
		mainSelector: {
			type: Boolean,
			default: false
		},
		panel: {
			type: Object
		}
	},

	data() {
		return {
			selectedStation: null,
			selectorOpen: false,
			panelLocationType: null,
			locationChangeDisposable: null
		};
	},

	computed: {
		config() {
			return config;
		},

		liveMode() {
			return this.$store.getters.liveMode();
		},

		liveModeSelectedStation() {
			return this.$store.getters.liveModeSelectedStation();
		},

		organizationId() {
			return this.$store.getters.getOrgID();
		},

		stations() {
			return this.$store.getters.getStations();
		},

		stationsItems() {
			return _.uniqBy(this.selectedStations.concat(this.favoriteStations, this.filteredStations), "StationID");
		},

		xlAndUpScreen() {
			return this.$vuetify.display.xlAndUp;
		},

		menuProps() {
			const props = {
				closeOnContentClick: false,
				contentClass: "rounded-sm",
				minHeight: 64,
				maxHeight: 512,
				width: 300,
				offset: [0, 60]
			};

			let attach = false;
			if (this.panelLocationType === "popout") {
				const popoutWindow = this.panel.api.getWindow();
				attach = popoutWindow?.document?.body || false;
			}

			props.attach = attach;
			return props;
		}
	},

	watch: {
		selectedStation: {
			async handler(station, previousSelectedStation) {
				const sameStation = station?.StationID === previousSelectedStation?.StationID;
				// if it's the same station, but we only have the object from the portal api,
				// restore the enriched one (with the properties from the ZC api) from the store and return
				if (sameStation) {
					const enriched = this.liveModeSelectedStation;
					if (enriched && !_.isEqual(station, enriched)) {
						this.selectedStation = enriched;
					}
					return;
				}

				this.$store.dispatch("getUserFeatures", {object_id: station.StationID});
				if (previousSelectedStation) {
					this.$store.commit("removeEnabledFeatures", previousSelectedStation.StationID);
					this.$store.commit("removeSelectedStation", previousSelectedStation.StationID);
				}

				if (!this.liveMode) {
					if (this.panel.params.selectedStationID !== station.StationID) {
						this.panel.api.updateParameters({selectedStationID: station.StationID});
					}
					return;
				}

				//On Live Mode only
				if (station && previousSelectedStation) {
					this.$store.dispatch("stationSubscription", {
						stationId: previousSelectedStation.StationID,
						subscribe: false
					});
					this.$store.dispatch(
						"clearSequencerSnapshotExtendRequestInterval",
						previousSelectedStation.StationID
					);
					this.$store.dispatch("sequencerSnapshotSubscription", {
						stationID: previousSelectedStation.StationID,
						subscribe: false
					});
					this.$store.dispatch("livePlayerSnapshotSubscription", false);
					this.$store.dispatch("livePlayerConferenceSubscription", false);
					this.$store.dispatch("livePlayerLiveMetadataSubscription", false);
					this.$store.dispatch("playerOnlineSubscription", false);
					this.$store.dispatch("playerHeartbeatSubscription", false);
					this.$store.dispatch("closeWebRTCConnection", true);
					this.$store.dispatch("resetState");
					this.studioConnecting = false;
					this.studioDisconnecting = false;
					this.$worker.clearTimeout(this.currentShowTimeout);
				}

				if (station) {
					this.$store
						.dispatch("getStation", station)
						.then(async station => {
							this.$store.commit("setLiveModeSelectedStationID", station.StationID);
							this.stationSearchTerm = null;
							this.reInitializeData();
							await this.$store.dispatch("getAstrumPlayers");
							this.$store.dispatch("stationSubscription", {stationID: station.StationID});

							this.$store.dispatch("startSequencerSnapshotExtendRequestInterval", station.StationID);
							this.$store.dispatch("sequencerSnapshotSubscription", {stationID: station.StationID});

							this.$store.dispatch("livePlayerSnapshotSubscription");
							this.$store.dispatch("livePlayerConferenceSubscription");
							this.$store.dispatch("livePlayerLiveMetadataSubscription");
							this.$store.dispatch("playerOnlineSubscription");
							this.$store.dispatch("playerHeartbeatSubscription");

							this.$store.dispatch("sequencerSnapshotRequest", station.StationID);
							this.$store.dispatch("livePlayerSnapshotRequest");
						})
						.catch(error => {
							this.selectedStation = null;
							console.error(error);
						});
				}
				this.selectorOpen = false;
			},
			immediate: true
		},

		"stations.loading"(loading) {
			// Load user's favorite stations after stations have been loaded (and the user is not searching for a station)
			if (!loading && !this.stationSearchTerm && !this.favoriteStations.length) {
				this.loadUserFavoriteStations();
			}
		}
	},

	mounted() {
		const dockViewApi = this.panel?.api;
		if (dockViewApi) {
			this.panelLocationType = dockViewApi.location?.type ?? "grid";
			// Subscribe to location change events so we can react whenever the panel moves
			// (e.g., when it is popped out into a new window or docked back)
			this.locationChangeDisposable = dockViewApi.onDidLocationChange(event => {
				this.panelLocationType = event.location?.type ?? "grid";
			});
		}

		if (this.liveMode) {
			if (this.mainSelector) {
				if (this.liveModeSelectedStation) {
					this.selectedStation = this.liveModeSelectedStation;
				} else {
					this.focusOnSelectorInput();
				}

				emitter.on("set-selected-station", station => {
					this.selectedStation = station;
				});

				emitter.on("user-objects-loaded", () => {
					this.loadStations();
				});

				emitter.on("preload-user-stations", () => {
					this.preloadStations = true;
					this.loadStations();
				});
			}
		} else {
			if (this.panel.params.selectedStationID) {
				this.selectStation({StationID: this.panel.params.selectedStationID});
			}
		}
	},

	beforeUnmount() {
		// clean up the location change listener (prevents memory leaks and weird updates after unmount)
		if (this.locationChangeDisposable) {
			this.locationChangeDisposable.dispose();
			this.locationChangeDisposable = null;
		}
		if (this.mainSelector) {
			emitter.off("set-selected-station");
			emitter.off("user-objects-loaded");
			emitter.off("preload-user-stations");
		}
	},

	methods: {
		async selectStation(station) {
			this.selectedStation = await this.$store.dispatch("getStation", station);
			if (this.selectedStation) {
				this.selectedStations = {
					StationID: this.selectedStation.StationID,
					StationName: this.selectedStation.StationName,
					Logo: this.selectedStation.Logo,
					logo: this.selectedStation.Logo
				};
			}
			if (this.liveMode) {
				this.$store.commit("setLiveModeSelectedStationID", station.StationID);
			}
			this.selectorOpen = false;
		},

		focusOnSelectorInput() {
			this.selectorOpen = true;
			this.loadStations();
		},

		loadUserFavoriteStations() {
			// Favorite stations that have not been fetched yet
			const favoriteNotLoadedStations = _.difference(this.stations.favorites, _.map(this.filteredStations, "id"));

			if (favoriteNotLoadedStations.length > 0) {
				this.$store
					.dispatch("getStations", {
						search: favoriteNotLoadedStations.join(" OR "),
						feature: this.panel?.params?.panelFeature
					})
					.then(stations => {
						stations.forEach(s => {
							s.favorite = true;
						});
						this.favoriteStations.push(...stations);
					})
					.catch(() =>
						this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
							message: "An error occurred when trying to load user's favorite stations."
						})
					);
			}
		}
	}
};
</script>

<style scoped lang="scss">
.station-menu-container :deep() {
	.v-field {
		.v-label.v-field-label {
			color: $grey-600;
			--v-medium-emphasis-opacity: 1;
		}

		&.v-theme--dark {
			color: $ihm-white;

			.v-label.v-field-label {
				color: $ihm-white;
			}
		}
	}

	.v-icon.v-theme--dark {
		color: $ihm-white;
	}

	.v-field__input {
		cursor: pointer;
		padding-left: 0;
		padding-right: 0;
	}

	.station-name {
		display: -webkit-box;
		overflow: hidden;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
	}
}

:deep(#station-control-text) {
	color: $grey-300;
	font-weight: 600;
}
</style>
